import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Radio } from 'lucide-react';
import { API } from '../config';

/**
 * Subtle single-row marquee for Home page. Shows masked recent winners as a
 * tiny scrolling ribbon — NOT a dominant section.
 * Auto-hides when feed is empty. Polls every 15s (gentler than the full feed).
 */
export const WinnerTicker = () => {
  const [items, setItems] = useState([]);
  const [visible, setVisible] = useState(typeof document === 'undefined' ? true : !document.hidden);
  const timerRef = useRef(null);

  const fetchFeed = async () => {
    try {
      const { data } = await axios.get(`${API}/kuji/feed`, { params: { limit: 12 } });
      setItems(data || []);
    } catch (_e) {
      // silently ignore
    }
  };

  useEffect(() => {
    const onVis = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!visible) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }
    fetchFeed();
    timerRef.current = setInterval(fetchFeed, 15000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [visible]);

  if (items.length === 0) return null;

  // Duplicate items so the marquee loops smoothly
  const repeated = [...items, ...items];

  return (
    <div
      className="overflow-hidden py-2"
      style={{ background: 'rgba(212,175,55,0.05)', borderTop: '1px solid rgba(212,175,55,0.15)', borderBottom: '1px solid rgba(212,175,55,0.15)' }}
      data-testid="winner-ticker"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center gap-3">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Radio className="w-3 h-3 animate-pulse" style={{ color: '#EF4444' }} />
          <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#D4AF37' }}>Live</span>
        </div>
        <div className="flex-1 overflow-hidden relative">
          <div className="flex gap-8 animate-marquee whitespace-nowrap">
            {repeated.map((it, idx) => (
              <span key={`${it.id}-${idx}`} className="text-xs" style={{ color: '#A3B8AC' }}>
                <span style={{ color: '#F8FAF9' }}>{it.username_masked}</span>
                {' won '}
                <span style={{ color: '#D4AF37' }}>{it.prize_name_snapshot}</span>
                {' · '}
                <span className="opacity-70">{it.event_name_snapshot}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 35s linear infinite;
        }
      `}</style>
    </div>
  );
};
